import { useEffect, useState, useRef } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FolderLock, Upload, Download, Pencil, Trash2, FileText, Image as ImageIcon, FileArchive, FileSpreadsheet, FileType2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const formatBytes = (n) => {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / (1024 * 1024)).toFixed(2)} MB`;
};

const fileIcon = (ct, name) => {
  const c = String(ct || "").toLowerCase();
  const n = String(name || "").toLowerCase();
  if (c.startsWith("image/")) return ImageIcon;
  if (c.includes("pdf") || n.endsWith(".pdf")) return FileType2;
  if (c.includes("zip") || n.endsWith(".zip")) return FileArchive;
  if (c.includes("excel") || c.includes("spreadsheet") || /\.(xlsx?|csv)$/.test(n)) return FileSpreadsheet;
  return FileText;
};

export default function AgencyAttachments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingFile, setPendingFile] = useState(null); // { file, customName, description }
  const [renameTarget, setRenameTarget] = useState(null); // { id, original_filename, description }
  const [busy, setBusy] = useState(false);
  // 🆕 22/07/2026 — Stato download per file: { [id]: { pct, loadedMB, totalMB } }
  const [downloading, setDownloading] = useState({});
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency-attachments");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFilePicked = (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File troppo grande (max 25 MB)");
      return;
    }
    // Suggerisci come nome quello attuale (senza estensione) — l'utente può rinominare
    const dot = file.name.lastIndexOf(".");
    const nameOnly = dot > 0 ? file.name.slice(0, dot) : file.name;
    setPendingFile({ file, customName: nameOnly, description: "" });
  };

  const confirmUpload = async () => {
    if (!pendingFile?.file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile.file);
      const params = new URLSearchParams();
      if (pendingFile.customName?.trim()) params.set("custom_filename", pendingFile.customName.trim());
      if (pendingFile.description?.trim()) params.set("description", pendingFile.description.trim());
      await api.post(`/agency-attachments?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allegato caricato");
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadOne = async (a) => {
    // Guard: già in download
    if (downloading[a.id]) return;
    const filename = a.original_filename || "allegato";
    const toastId = toast.loading(`Preparazione download…`, {
      description: filename,
      duration: Infinity,
    });
    setDownloading((s) => ({ ...s, [a.id]: { pct: 0, loadedMB: 0, totalMB: null } }));
    try {
      const res = await api.get(`/agency-attachments/${a.id}/download`, {
        responseType: "blob",
        onDownloadProgress: (e) => {
          const loaded = e.loaded || 0;
          const total = e.total || a.size || 0;
          const pct = total > 0 ? Math.round((loaded / total) * 100) : null;
          const loadedMB = loaded / (1024 * 1024);
          const totalMB = total > 0 ? total / (1024 * 1024) : null;
          setDownloading((s) => ({ ...s, [a.id]: { pct, loadedMB, totalMB } }));
          const humanLoaded = loadedMB >= 1 ? `${loadedMB.toFixed(2)} MB` : `${(loaded / 1024).toFixed(0)} KB`;
          const humanTotal = totalMB != null ? (totalMB >= 1 ? `${totalMB.toFixed(2)} MB` : `${(total / 1024).toFixed(0)} KB`) : "?";
          toast.loading(
            pct != null ? `Download in corso · ${pct}%` : `Download in corso…`,
            { id: toastId, description: `${filename} · ${humanLoaded} / ${humanTotal}` }
          );
        },
      });
      const blob = new Blob([res.data], { type: a.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Scaricato: ${filename}`, { id: toastId, duration: 3000 });
    } catch (e) {
      toast.error(formatApiError(e), { id: toastId, duration: 5000 });
    } finally {
      setDownloading((s) => {
        const next = { ...s };
        delete next[a.id];
        return next;
      });
    }
  };

  const saveRename = async () => {
    if (!renameTarget) return;
    setBusy(true);
    try {
      await api.patch(`/agency-attachments/${renameTarget.id}`, {
        original_filename: renameTarget.original_filename,
        description: renameTarget.description || "",
      });
      toast.success("Allegato aggiornato");
      setRenameTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeOne = async (a) => {
    if (!confirm(`Eliminare l'allegato "${a.original_filename}"?`)) return;
    try {
      await api.delete(`/agency-attachments/${a.id}`);
      toast.success("Allegato eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="agency-attachments-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <FolderLock className="h-8 w-8 sm:h-10 sm:w-10 text-indigo-600 bg-indigo-100 rounded-lg p-1.5" />
            Allegati Agenzia
          </h1>
          <p className="text-slate-500 mt-1">
            🔒 Sezione privata — visibile solo al personale di questa agenzia.
            <span className="ml-2 text-xs text-slate-400">{items.length} {items.length === 1 ? "allegato" : "allegati"}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            data-testid="agency-att-input"
            onChange={(e) => onFilePicked(e.target.files?.[0])}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
            className="hidden"
          />
          <Button
            data-testid="agency-att-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Upload className="h-4 w-4 mr-2" /> Carica file
          </Button>
        </div>
      </div>

      {loading && <div className="text-slate-500">Caricamento…</div>}

      {!loading && items.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-500">
          <FolderLock className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          Nessun allegato ancora caricato.<br />
          <span className="text-sm">Usa il pulsante <strong>Carica file</strong> per aggiungere documenti privati dell&apos;agenzia.</span>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Nome file</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Descrizione</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Dimensione</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Caricato il</th>
                <th className="text-left px-4 py-2 font-medium text-slate-600">Da</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => {
                const Icon = fileIcon(a.content_type, a.original_filename);
                return (
                  <tr key={a.id} data-testid={`agency-att-row-${a.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                        <span className="font-medium truncate" title={a.original_filename}>{a.original_filename}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-[280px] truncate" title={a.description || ""}>
                      {a.description || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 tabular-nums">{formatBytes(a.size)}</td>
                    <td className="px-4 py-2.5 text-slate-500 tabular-nums">{formatDateTime(a.created_at)}</td>
                    <td className="px-4 py-2.5 text-slate-500">{a.uploaded_by_name || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex flex-col items-end gap-1">
                        <div className="inline-flex gap-1">
                          <button
                            data-testid={`agency-att-download-${a.id}`}
                            onClick={() => downloadOne(a)}
                            disabled={!!downloading[a.id]}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-700 disabled:opacity-50 disabled:cursor-wait relative"
                            title={downloading[a.id] ? `Download in corso… ${downloading[a.id].pct ?? 0}%` : "Scarica"}
                          >
                            {downloading[a.id] ? (
                              <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            data-testid={`agency-att-rename-${a.id}`}
                            onClick={() => setRenameTarget({ id: a.id, original_filename: a.original_filename, description: a.description || "" })}
                            className="p-1.5 rounded hover:bg-amber-50 text-amber-700"
                            title="Rinomina/modifica"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`agency-att-delete-${a.id}`}
                            onClick={() => removeOne(a)}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                            title="Elimina"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {downloading[a.id] && (
                          <div className="w-24" data-testid={`agency-att-download-progress-${a.id}`}>
                            <div className="h-1 bg-slate-200 rounded overflow-hidden">
                              <div
                                className="h-full bg-indigo-500 transition-all duration-150"
                                style={{ width: `${downloading[a.id].pct ?? 0}%` }}
                              />
                            </div>
                            <div className="text-[9px] text-slate-500 mt-0.5 tabular-nums text-right">
                              {downloading[a.id].pct != null ? `${downloading[a.id].pct}%` : `${downloading[a.id].loadedMB.toFixed(1)} MB`}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog upload con possibilità di rinominare prima di salvare */}
      <Dialog open={!!pendingFile} onOpenChange={(o) => { if (!o && !busy) setPendingFile(null); }}>
        <DialogContent className="max-w-md" data-testid="agency-att-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-indigo-600" /> Carica allegato agenzia
            </DialogTitle>
          </DialogHeader>
          {pendingFile && (
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
                File originale: <strong>{pendingFile.file.name}</strong> · {formatBytes(pendingFile.file.size)}
              </div>
              <div>
                <Label className="text-xs">Nome file (puoi rinominarlo)</Label>
                <Input
                  data-testid="agency-att-custom-name"
                  value={pendingFile.customName}
                  onChange={(e) => setPendingFile({ ...pendingFile, customName: e.target.value })}
                  placeholder="Nome file (senza estensione)"
                  autoFocus
                />
                <p className="text-[10px] text-slate-500 mt-0.5">L&apos;estensione originale viene mantenuta automaticamente.</p>
              </div>
              <div>
                <Label className="text-xs">Descrizione (opzionale)</Label>
                <Input
                  data-testid="agency-att-description"
                  value={pendingFile.description}
                  onChange={(e) => setPendingFile({ ...pendingFile, description: e.target.value })}
                  placeholder="Es. Modulistica, contratti, listini…"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingFile(null)} disabled={busy}>Annulla</Button>
            <Button
              data-testid="agency-att-upload-confirm"
              onClick={confirmUpload}
              disabled={busy || !pendingFile?.customName?.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Upload className="h-4 w-4 mr-2" /> {busy ? "Caricamento…" : "Carica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog rename/edit */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => { if (!o && !busy) setRenameTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="agency-att-rename-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-amber-600" /> Rinomina allegato
            </DialogTitle>
          </DialogHeader>
          {renameTarget && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nome file</Label>
                <Input
                  data-testid="agency-att-rename-name"
                  value={renameTarget.original_filename}
                  onChange={(e) => setRenameTarget({ ...renameTarget, original_filename: e.target.value })}
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs">Descrizione</Label>
                <Input
                  data-testid="agency-att-rename-description"
                  value={renameTarget.description}
                  onChange={(e) => setRenameTarget({ ...renameTarget, description: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} disabled={busy}>Annulla</Button>
            <Button
              data-testid="agency-att-rename-save"
              onClick={saveRename}
              disabled={busy || !renameTarget?.original_filename?.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
